import { createErrorToastMapper, type SaaSConfig } from '@xhubio-saas/core-frontend/kernel'
import { SettingsAccountPageWithProviders } from './app.pages'
import { plugins } from './app.plugins.js'
import { AuthBrandPanel } from './components/AuthBrandPanel'
import { BillingEntitlementProvider } from './components/BillingEntitlementProvider'
import { LocaleWrapper } from './components/LocaleWrapper'
import { OnboardingCompletionListener } from './components/OnboardingCompletionListener'
import { PdfApiLogo } from './components/PdfApiLogo'
import { withConsentGuard } from './components/withConsentGuard'
import { redirectTo } from './layouts/redirectTo'
import { ForgotPasswordPage } from './pages/auth/ForgotPasswordPage'
import { LoginPage } from './pages/auth/LoginPage'
import { RegisterPage } from './pages/auth/RegisterPage'
import { VerifyEmailPage } from './pages/auth/VerifyEmailPage'
import { ProfilePage } from './pages/protected/ProfilePage'
import { SettingsAppearancePage } from './pages/protected/settings/SettingsAppearancePage'
import { SettingsPasswordPage } from './pages/protected/settings/SettingsPasswordPage'
import { SettingsProfilePage } from './pages/protected/settings/SettingsProfilePage'
import { SettingsSystemPage } from './pages/protected/settings/SettingsSystemPage.js'
import { CookiePolicyPage } from './pages/public/CookiePolicyPage'
import { ImprintPage } from './pages/public/ImprintPage'
import { PrivacyPage } from './pages/public/PrivacyPage'
import { TermsPage } from './pages/public/TermsPage'

// Wrap protected pages with consent guard
const ProtectedProfilePage = withConsentGuard(ProfilePage)
const ProtectedSettingsProfilePage = withConsentGuard(SettingsProfilePage)
const ProtectedSettingsPasswordPage = withConsentGuard(SettingsPasswordPage)
const ProtectedSettingsAppearancePage = withConsentGuard(SettingsAppearancePage)
const ProtectedSettingsAccountPage = withConsentGuard(SettingsAccountPageWithProviders)
const ProtectedSettingsSystemPage = withConsentGuard(SettingsSystemPage)

function getApiUrl(): string {
  // Runtime config takes precedence (set at container startup)
  if (typeof window !== 'undefined' && window.__RUNTIME_CONFIG__?.API_URL) {
    return window.__RUNTIME_CONFIG__.API_URL.replace(/\/+$/, '')
  }
  // Build-time env var (for local development)
  // Empty string means "use relative URLs" (goes through Vite proxy)
  if (import.meta.env.VITE_API_URL !== undefined) {
    return import.meta.env.VITE_API_URL.replace(/\/+$/, '')
  }
  // In test environment, return empty string
  if (import.meta.env.MODE === 'test' || typeof window === 'undefined') {
    return ''
  }
  throw new Error('API URL not configured. Set VITE_API_URL or provide runtime config.')
}

/**
 * SaaS Application Configuration
 *
 * This configuration is used by the frontend-kernel to bootstrap
 * the application with all necessary providers and features.
 */
// biome-ignore lint/suspicious/noExplicitAny: SaaSConfig is parameterised via this open record so plugins can extend it
export const saasConfig: SaaSConfig & Record<string, any> = {
  // ═══════════════════════════════════════════════════════════════════════════
  // Branding
  // ═══════════════════════════════════════════════════════════════════════════
  name: 'pdf-api.xhub.io',
  logo: PdfApiLogo,

  // ═══════════════════════════════════════════════════════════════════════════
  // API Configuration
  // ═══════════════════════════════════════════════════════════════════════════
  api: {
    baseUrl: getApiUrl(),
    trpcPath: '/trpc',
    /**
     * Ohne Mapper reicht der Kernel die **rohe Backend-Meldung** an den Toast
     * durch — englische Entwicklersaetze und Zod-JSON inklusive (Plan 131 S5).
     *
     * Bewusst **ohne** Feld-Beschriftungen: die gaebe es hier nur doppelt, die
     * Klartexte stehen schon an den Formularen.
     */
    mapErrorToast: createErrorToastMapper({ devWarnings: import.meta.env.DEV }),
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Authentication
  // ═══════════════════════════════════════════════════════════════════════════
  auth: {
    providers: ['email', 'google', 'github'],
    emailVerification: true,
    allowRegistration: true,
    // pdf-api owns its auth screens; the kernel routes to these instead of
    // its built-in forms. Needs core-frontend >= 3.36.0 — on 3.35.0 the key is
    // ignored and the built-in forms render, so this degrades rather than
    // breaks. The other three apps keep the kernel default.
    //
    // Owning `register` means owning the consent capture: RegisterPage mirrors
    // the kernel's contract (terms + privacy mandatory → `consentTypes`). If
    // the required documents change, both must change.
    components: {
      login: LoginPage,
      register: RegisterPage,
      forgotPassword: ForgotPasswordPage,
      verifyEmail: VerifyEmailPage,
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Theme
  // ═══════════════════════════════════════════════════════════════════════════
  theme: {
    defaultTheme: 'system',
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Languages (European languages for the pdf-api application)
  // ═══════════════════════════════════════════════════════════════════════════
  languages: {
    default: 'en',
    active: [
      'en', // English
      'de', // German
      'nl', // Dutch
      'cs', // Czech
      'bg', // Bulgarian
      'fr', // French
      'es', // Spanish
      'it', // Italian
      'pl', // Polish
      'pt', // Portuguese
      'hu', // Hungarian
      'ro', // Romanian
    ],
    // Der Begriff dieser App für „die Organisation, in der der Anwender ist"
    // (Plan CORE 2026-09-14/01 S4): **„Organisation"** — bewusst das
    // Rahmenwerkswort, bis jemand anderes entscheidet. Steht ausdrücklich hier,
    // damit die Stelle existiert und nicht still der Kernel-Rückfall greift;
    // tests/organisationsbegriff.test.ts prüft, dass beide dasselbe Wort führen.
    // Nur de/en — die übrigen Sprachen fallen auf den Kernel zurück, der
    // dasselbe Wort führt. Formen mit Bestimmwort nach `OrganizationTermForms`.
    organizationTerm: {
      de: {
        nominativ: 'die Organisation',
        genitiv: 'dieser Organisation',
        dativ: 'dieser Organisation',
        akkusativ: 'die Organisation',
        kompositum: 'Organisations',
      },
      en: {
        nominativ: 'the organization',
        genitiv: 'of this organization',
        dativ: 'this organization',
        akkusativ: 'the organization',
        kompositum: 'organization',
      },
    },
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Features
  // ═══════════════════════════════════════════════════════════════════════════
  features: {
    quickActions: true,
    preferences: true,
    breadcrumbs: true,
    organizationSwitcher: false,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Layout
  // ═══════════════════════════════════════════════════════════════════════════
  layout: {
    defaultRoute: '/pdf/v2/templates',
    // Kein Footer im eingeloggten Bereich: seine vier Punkte (Impressum,
    // Datenschutz, AGB, Cookie-Einstellungen) wohnen in Einstellungen → System
    // → Rechtliches (SettingsSystemPage). Login/Register/Public behalten den
    // Footer — Impressumspflicht, und dort sitzen Sprach-/Theme-Wahl für
    // Ausgeloggte. footerLinks/copyright unten bleiben dafür erhalten.
    hideProtectedFooter: true,
    // Das Avatar-Menue fuehrt nur das Persoenliche; das Organisatorische steht
    // in der Seitenleiste unter „Organisation → System".
    userMenu: { settingsLabelKey: 'common.myAccount' },
    // Split-screen for the auth screens: AuthLayout renders this as the left
    // lg:w-1/2 column and drops it below lg, so the form owns the phone. Needs
    // no kernel change — the hook has existed since 3.34.0 (hausverwaltung uses
    // it too) and narrows the form column to max-w-md when set.
    authAside: AuthBrandPanel,
    sidebar: {
      upgrade: {
        enabled: true,
        href: '/subscription',
        labelKey: 'common:sidebar.upgrade',
      },
    },
    footerLinks: [
      { labelKey: 'common.imprint', href: '/imprint' },
      { labelKey: 'common.privacy', href: '/privacy' },
      { labelKey: 'common.terms', href: '/terms' },
    ],
    copyright: `${new Date().getFullYear()} pdf-api.xhub.io`,
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // Wrappers (locale provider + billing entitlements for RBAC + billing check)
  // ═══════════════════════════════════════════════════════════════════════════
  // LocaleWrapper outermost so the router tree can `useLocale()` for
  // date/number/currency formatting that adapts to the active org's country.
  // OnboardingCompletionListener bridges the YAML plugin's window event to
  // setPreference + useNavigate (must be inside the router + tRPC providers).
  wrappers: [LocaleWrapper, BillingEntitlementProvider, OnboardingCompletionListener],

  // ═══════════════════════════════════════════════════════════════════════════
  // Plugins
  // ═══════════════════════════════════════════════════════════════════════════
  plugins,

  // ═══════════════════════════════════════════════════════════════════════════
  // Routes
  // ═══════════════════════════════════════════════════════════════════════════
  routes: [
    // Public routes
    { path: '/terms', component: TermsPage, layout: 'public' },
    { path: '/privacy', component: PrivacyPage, layout: 'public' },
    { path: '/imprint', component: ImprintPage, layout: 'public' },
    { path: '/cookie-policy', component: CookiePolicyPage, layout: 'public' },

    // Protected routes (wrapped with ConsentGuard)
    { path: '/profile', component: ProtectedProfilePage, layout: 'protected' },
    {
      path: '/settings',
      component: ProtectedSettingsProfilePage,
      layout: 'protected',
    },
    {
      path: '/settings/profile',
      component: ProtectedSettingsProfilePage,
      layout: 'protected',
    },
    {
      path: '/settings/password',
      component: ProtectedSettingsPasswordPage,
      layout: 'protected',
    },
    {
      path: '/settings/appearance',
      component: ProtectedSettingsAppearancePage,
      layout: 'protected',
    },
    {
      path: '/settings/account',
      component: ProtectedSettingsAccountPage,
      layout: 'protected',
    },
    // „System" gehoert der Organisation und liegt deshalb nicht mehr unter
    // `/settings` (= „Mein Konto" am Avatar). Die alten Adressen leiten weiter —
    // Lesezeichen, Support-Antworten und Changelog-Links zeigen noch dorthin.
    {
      path: '/system',
      component: ProtectedSettingsSystemPage,
      layout: 'protected',
    },
    { path: '/settings/system', component: redirectTo('/system'), layout: 'protected' },
    { path: '/settings/legal', component: redirectTo('/system/legal'), layout: 'protected' },
  ],
}
